<script setup lang="ts">
import { getDirectusAssetURL } from '@@/server/utils/directus-utils';
import type { Wizard, WizardStep, WizardStepQuestion, FormField, WizardResult } from '#shared/types/schema';
import BaseFormField from '../forms/BaseFormField.vue';
import BaseButton from '../base/BaseButton.vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { buildZodSchema } from '~/lib/zodSchemaBuilder';
import { translateZodSchema } from '~/lib/zodSchemaTranslator';
import { z } from 'zod';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle } from 'lucide-vue-next';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';

interface WizardBlockData {
	id: string;
	wizard?: Wizard | string | null;
}

const props = defineProps<{
	data: WizardBlockData;
	pageId?: string;
}>();

const { user } = useAuth();

const { setAttr } = useVisualEditing();
const runtimeConfig = useRuntimeConfig();

// Recupera i dati del sito per il logo
const { data: siteData } = await useFetch('/api/site-data', {
	key: 'site-data',
});

// Logo URLs (stesso comportamento della navbar non trasparente)
const lightLogoUrl = computed(() =>
	siteData.value?.globals?.logo ? `${runtimeConfig.public.directusUrl}/assets/${siteData.value.globals.logo}` : '/images/logo.svg',
);

const darkLogoUrl = computed(() =>
	siteData.value?.globals?.logo_dark_mode ? `${runtimeConfig.public.directusUrl}/assets/${siteData.value.globals.logo_dark_mode}` : '',
);

// Stato del wizard
const wizard = ref<Wizard | null>(null);
const currentStepIndex = ref<number>(-1); // -1 = step iniziale, 0+ = step del questionario
const sessionSecretHash = ref<string | null>(null);
const submissionId = ref<number | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const result = ref<WizardResult | null>(null);
const showContactForm = ref(false);
const isSubmittingContact = ref(false);
const contactSubmitted = ref(false);

// Ref per il container scrollabile del wizard
const wizardContainer = ref<HTMLElement | null>(null);

// Funzione helper per scrollare all'inizio
const scrollToTop = () => {
	nextTick(() => {
		if (wizardContainer.value) {
			wizardContainer.value.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	});
};

// Funzione helper per ripristinare lo zoom quando un campo perde il focus
const handleInputBlur = () => {
	if (typeof window !== 'undefined' && window.visualViewport) {
		// Ripristina lo zoom a 1.0 dopo un breve delay per permettere al browser di completare il blur
		setTimeout(() => {
			if (window.visualViewport && window.visualViewport.scale !== 1) {
				// Forza il ripristino dello zoom
				document.body.style.zoom = '1';
				setTimeout(() => {
					document.body.style.zoom = '';
				}, 100);
			}
		}, 100);
	}
};

// Form per le domande dello step corrente
const currentStepQuestions = computed(() => {
	if (!wizard.value || currentStepIndex.value < 0) return [];
	const step = wizard.value.steps?.[currentStepIndex.value];
	if (!step || typeof step === 'string') return [];
	return (step as WizardStep).questions || [];
});

const currentStep = computed(() => {
	if (!wizard.value || currentStepIndex.value < 0) return null;
	const step = wizard.value.steps?.[currentStepIndex.value];
	return typeof step === 'object' ? step : null;
});

const isLastStep = computed(() => {
	if (!wizard.value || !wizard.value.steps) return false;
	return currentStepIndex.value === wizard.value.steps.length - 1;
});

const isFirstStep = computed(() => currentStepIndex.value === 0);

// Prepara i campi del form per lo step corrente, ordinati per sort
const formFields = computed(() => {
	const questions = currentStepQuestions.value;
	return questions
		.map((q) => {
			if (typeof q === 'string' || !q.form_field) return null;
			const formField = typeof q.form_field === 'object' ? q.form_field : null;
			return formField;
		})
		.filter((f): f is FormField => f !== null)
		.sort((a, b) => (a.sort || 0) - (b.sort || 0));
});

// Crea lo schema di validazione per i campi usando le utility esistenti
const validFields = computed(() =>
	formFields.value.filter((field): field is FormField & { name: string } => field.name != null && field.name !== ''),
);

const validationSchema = computed(() => {
	if (!validFields.value.length) return null;
	try {
		// Crea lo schema originale
		const originalSchema = buildZodSchema(validFields.value);
		
		// Traduci lo schema in italiano
		const translatedSchema = translateZodSchema(originalSchema, validFields.value);
		
		const schema = toTypedSchema(translatedSchema);
		return schema;
	} catch (error) {
		console.error('Error creating validation schema:', error);
		return null;
	}
});

const initialValues = computed(() => {
	if (!validFields.value.length) return {};
	return validFields.value.reduce(
		(defaults, field) => {
			const name = field.name;

			switch (field.type) {
				case 'checkbox':
					defaults[name] = false;
					break;
				case 'checkbox_group':
					defaults[name] = [];
					// Aggiungi campo per "Altro" se presente nelle choices
					if (field.choices?.some((c) => c.value === 'other')) {
						defaults[`${name}_other`] = '';
					}
					break;
				case 'select':
				case 'radio':
					defaults[name] = '';
					// Aggiungi campo per "Altro" se presente nelle choices
					if (field.choices?.some((c) => c.value === 'other')) {
						defaults[`${name}_other`] = '';
					}
					break;
				case 'file':
					defaults[name] = null;
					break;
				case 'textarea':
				case 'text':
				default:
					defaults[name] = '';
			}

			return defaults;
		},
		{} as Record<string, any>,
	);
});

const { handleSubmit, values, resetForm, setFieldValue, validate, setFieldError } = useForm({
	validationSchema: computed(() => validationSchema.value),
	initialValues: initialValues.value,
});

// Valida manualmente i campi obbligatori
const validateRequiredFields = (formValues: Record<string, any>): { valid: boolean; errors: Record<string, string> } => {
	const errors: Record<string, string> = {};
	
	for (const field of validFields.value) {
		if (!field.required || !field.name) continue;
		
		const value = formValues[field.name];
		let isValid = true;
		
		if (field.type === 'checkbox_group') {
			isValid = Array.isArray(value) && value.length > 0;
		} else if (field.type === 'checkbox') {
			isValid = value === true;
		} else if (field.type === 'radio' || field.type === 'select') {
			isValid = value !== '' && value !== null && value !== undefined;
		} else {
			// text, textarea, etc.
			isValid = value !== '' && value !== null && value !== undefined;
		}
		
		if (!isValid) {
			errors[field.name] = `${field.label || field.name} è obbligatorio`;
		}
	}
	
	return {
		valid: Object.keys(errors).length === 0,
		errors,
	};
};

// Crea la funzione di submit wrapper come in DynamicForm
const onSubmitForm = async (e?: Event) => {
	if (e) {
		e.preventDefault();
	}
	
	if (isLoading.value) return;
	
	// Valida manualmente i campi obbligatori PRIMA di usare Zod
	const manualValidation = validateRequiredFields(values);
	
	if (!manualValidation.valid) {
		// Imposta gli errori nei campi usando setFieldError
		for (const [fieldName, errorMessage] of Object.entries(manualValidation.errors)) {
			setFieldError(fieldName, errorMessage);
		}
		
		// Scrolla al primo campo con errore
		await nextTick();
		const firstErrorField = Object.keys(manualValidation.errors)[0];
		const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
		if (errorElement) {
			errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
			(errorElement as HTMLElement).focus();
		}
		return;
	}
	
	// Valida anche con Zod per altre validazioni (email, min, max, etc.)
	const { valid, errors: validationErrors } = await validate();
	
	if (!valid) {
		// Scrolla al primo campo con errore
		if (validationErrors && Object.keys(validationErrors).length > 0) {
			await nextTick();
			const firstErrorField = Object.keys(validationErrors)[0];
			const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
			if (errorElement) {
				errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
				(errorElement as HTMLElement).focus();
			}
		}
		return;
	}
	
	// Se entrambe le validazioni passano, procedi con il salvataggio
	await saveStepAnswers(values);
};


// Flag per controllare se stiamo navigando indietro
const isGoingBack = ref(false);

// Aggiorna i valori iniziali quando cambiano i campi o lo step (solo se non stiamo andando indietro)
watch([initialValues, currentStepIndex], (newValues) => {
	if (isGoingBack.value) return; // Ignora il watcher quando si va indietro
	const [newInitialValues] = newValues;
	for (const [key, value] of Object.entries(newInitialValues)) {
		setFieldValue(key, value);
	}
}, { deep: true });

// Inizializza i campi "_other" quando vengono mostrati o quando cambiano i valori
watch(() => {
	// Crea un oggetto con tutti i valori attuali per triggerare il watcher
	return { ...values };
}, () => {
	// Per ogni campo che ha una choice "other", assicurati che il campo _other sia inizializzato
	for (const field of validFields.value) {
		if (field.choices?.some((c) => c.value === 'other')) {
			const otherFieldName = `${field.name}_other`;
			// Se il campo _other non esiste o è undefined, inizializzalo
			if (!(otherFieldName in values) || values[otherFieldName] === undefined) {
				setFieldValue(otherFieldName, '');
			}
		}
	}
}, { deep: true, immediate: true });

// Carica il wizard quando il componente viene montato
onMounted(async () => {
	const wizardId = typeof props.data.wizard === 'object' 
		? props.data.wizard.id 
		: props.data.wizard;

	if (!wizardId) {
		error.value = 'Wizard ID non trovato';
		return;
	}

	try {
		isLoading.value = true;
		const response = await $fetch<{ wizard: Wizard }>(`/api/wizard/${wizardId}`);
		wizard.value = response.wizard;
	} catch (err: any) {
		error.value = err.message || 'Errore nel caricamento del wizard';
	} finally {
		isLoading.value = false;
	}
});

// Avvia il wizard
const startWizard = async () => {
	if (!wizard.value || !wizard.value.steps || wizard.value.steps.length === 0) {
		error.value = 'Nessuno step disponibile';
		return;
	}

	isLoading.value = true;
	error.value = null;

	try {
		// Ottieni il primo step
		const firstStep = wizard.value.steps[0];
		if (!firstStep || typeof firstStep === 'string') {
			error.value = 'Primo step non trovato';
			return;
		}

		const firstStepId = typeof firstStep === 'object' ? firstStep.id : Number(firstStep);

		// Crea una submission iniziale per tracciare chi ha iniziato il wizard
		const response = await $fetch<{
			submission_id: number;
			session_secret_hash: string;
		}>('/api/wizard/submission', {
			method: 'POST',
			body: {
				wizard_id: wizard.value.id,
				step_id: firstStepId,
				answers: [], // Nessuna risposta, solo per tracciare l'inizio
				user_agent: navigator.userAgent,
				user_id: user.value?.id,
			},
		});

		// Salva il session_secret_hash per le submission successive
		sessionSecretHash.value = response.session_secret_hash;
		submissionId.value = response.submission_id;

		// Passa al primo step
		currentStepIndex.value = 0;
		// Reset form con i nuovi valori iniziali
		resetForm({
			values: initialValues.value,
		});
	} catch (err: any) {
		error.value = err.message || 'Errore nell\'avvio del wizard';
	} finally {
		isLoading.value = false;
	}
};

// Salva le risposte dello step corrente
const saveStepAnswers = async (formValues: Record<string, any>) => {
	if (!wizard.value || !currentStep.value) return;

	isLoading.value = true;
	error.value = null;

	try {
		// Prepara le risposte
		const answers = currentStepQuestions.value.map((q) => {
			if (typeof q === 'string' || !q.form_field) return null;
			const formField = typeof q.form_field === 'object' ? q.form_field : null;
			if (!formField || !formField.name) return null;

			const answerValue = formValues[formField.name];
			// Leggi anche il valore _other direttamente da values per assicurarti che sia aggiornato
			const otherValue = values[`${formField.name}_other`] || formValues[`${formField.name}_other`] || '';
			let answer: string = '';
			let answerJson: string | undefined;

			// Funzione helper per trovare la label di un value
			const findLabel = (value: string): string => {
				const choice = formField.choices?.find((c) => c.value === value);
				return choice?.text || value;
			};

			if (formField.type === 'checkbox_group' && Array.isArray(answerValue)) {
				// Per checkbox_group: answer contiene le label separate da a capo
				// Se "other" è selezionato, aggiungi il testo personalizzato
				const labels = answerValue.map((val) => {
					if (val === 'other') {
						return otherValue ? `Altro: ${otherValue}` : 'Altro';
					}
					return findLabel(val);
				});
				answer = labels.join('\n');
				
				// answer_json contiene array di oggetti { value, text, other_text? } in formato pretty
				const answerObjects = answerValue.map((val) => {
					if (val === 'other') {
						return {
							value: 'other',
							text: 'Altro',
							other_text: otherValue,
						};
					}
					return {
						value: val,
						text: findLabel(val),
					};
				});
				answerJson = JSON.stringify(answerObjects, null, 2);
			} else if (formField.type === 'checkbox') {
				// Per checkbox: answer contiene "Sì" o "No" (o la label se disponibile)
				if (formField.choices && formField.choices.length > 0) {
					answer = answerValue ? findLabel('true') : (formField.choices.find((c) => c.value === 'false')?.text || 'No');
				} else {
					answer = answerValue ? 'Sì' : 'No';
				}
				
				// answer_json contiene oggetto { value, text }
				const answerObject = {
					value: answerValue ? 'true' : 'false',
					text: answer,
				};
				answerJson = JSON.stringify(answerObject, null, 2);
			} else if (['radio', 'select'].includes(formField.type || '')) {
				// Per radio/select: answer contiene la label
				// Se è "other", aggiungi il testo personalizzato
				if (answerValue === 'other') {
					answer = otherValue ? `Altro: ${otherValue}` : 'Altro';
					
					// answer_json contiene oggetto { value, text, other_text } in formato pretty
					const answerObject = {
						value: 'other',
						text: 'Altro',
						other_text: otherValue,
					};
					answerJson = JSON.stringify(answerObject, null, 2);
				} else {
					answer = answerValue ? findLabel(String(answerValue)) : '';
					
					// answer_json contiene oggetto { value, text } in formato pretty
					if (answerValue) {
						const answerObject = {
							value: String(answerValue),
							text: answer,
						};
						answerJson = JSON.stringify(answerObject, null, 2);
					}
				}
			} else {
				// Per text, textarea, etc.: answer contiene il valore testuale
				answer = String(answerValue || '');
			}

			return {
				question_id: typeof q === 'object' ? q.id : Number(q),
				answer,
				answer_json: answerJson,
			};
		}).filter((a): a is { question_id: number; answer: string; answer_json: string | undefined } => a !== null);

		// Salva le risposte
		const response = await $fetch<{
			submission_id: number;
			session_secret_hash: string;
		}>('/api/wizard/submission', {
			method: 'POST',
			body: {
				wizard_id: wizard.value.id,
				session_secret_hash: sessionSecretHash.value || undefined,
				step_id: currentStep.value.id,
				answers,
				user_agent: navigator.userAgent,
				user_id: user.value?.id,
			},
		});

		sessionSecretHash.value = response.session_secret_hash;
		submissionId.value = response.submission_id;

		// Se è l'ultimo step, calcola il risultato
		if (isLastStep.value) {
			await calculateResult();
		} else {
			// Passa allo step successivo
			const nextIndex = currentStepIndex.value + 1;
			const nextStep = wizard.value.steps?.[nextIndex];
			
			currentStepIndex.value = nextIndex;
			
			// Aspetta che initialValues si aggiorni per il nuovo step
			await nextTick();
			
			// Recupera le risposte salvate per lo step successivo (se esistono)
			let savedValues: Record<string, any> = {};
			if (nextStep && typeof nextStep === 'object' && sessionSecretHash.value) {
				const nextStepQuestions = (nextStep.questions || []).filter(
					(q): q is WizardStepQuestion => typeof q === 'object'
				);
				savedValues = await loadSavedAnswersForStep(nextStep.id, nextStepQuestions);
			}
			
			// Combina i valori iniziali con quelli salvati
			const newInitialValues = { ...initialValues.value, ...savedValues };
			
			// Reset form con i valori combinati
			resetForm({
				values: newInitialValues,
			});
			
			// Forza l'aggiornamento dei valori per assicurarsi che siano sincronizzati
			await nextTick();
			for (const [key, val] of Object.entries(newInitialValues)) {
				setFieldValue(key, val);
			}
			
			// Scrolla all'inizio della pagina
			scrollToTop();
		}
	} catch (err: any) {
		error.value = err.message || 'Errore nel salvataggio delle risposte';
	} finally {
		isLoading.value = false;
	}
};

// Funzione helper per recuperare e mappare le risposte salvate per uno step
const loadSavedAnswersForStep = async (stepId: number, stepQuestions: WizardStepQuestion[]): Promise<Record<string, any>> => {
	if (!sessionSecretHash.value) return {};

	const savedValues: Record<string, any> = {};

	try {
		const response = await $fetch<{ answers: Array<{
			question: number;
			answer: string | null;
			answer_json: string | null;
		}> }>('/api/wizard/submission-answers', {
			query: {
				session_secret_hash: sessionSecretHash.value,
				step_id: stepId,
			},
		});

		// Mappa le risposte salvate ai campi del form
		if (response.answers && response.answers.length > 0) {
			for (const savedAnswer of response.answers) {
				const question = stepQuestions.find(
					(q) => (typeof q === 'object' ? q.id : Number(q)) === savedAnswer.question
				);
				
				if (question && typeof question === 'object' && question.form_field) {
					const formField = typeof question.form_field === 'object' ? question.form_field : null;
					
					if (formField && formField.name) {
						// Parsa l'answer_json se disponibile per ottenere i valori originali
						if (savedAnswer.answer_json) {
							try {
								const answerData = JSON.parse(savedAnswer.answer_json);
								
								if (formField.type === 'checkbox_group' && Array.isArray(answerData)) {
									// Per checkbox_group, estrai i valori
									savedValues[formField.name] = answerData.map((item: any) => item.value);
									// Se c'è "other", recupera anche il testo
									const otherItem = answerData.find((item: any) => item.value === 'other');
									if (otherItem && otherItem.other_text) {
										savedValues[`${formField.name}_other`] = otherItem.other_text;
									}
								} else if (formField.type === 'checkbox' && typeof answerData === 'object') {
									// Per checkbox, estrai il valore booleano
									savedValues[formField.name] = answerData.value === 'true';
								} else if (['radio', 'select'].includes(formField.type || '') && typeof answerData === 'object') {
									// Per radio/select, estrai il valore
									savedValues[formField.name] = answerData.value || '';
									// Se c'è "other", recupera anche il testo
									if (answerData.value === 'other' && answerData.other_text) {
										savedValues[`${formField.name}_other`] = answerData.other_text;
									}
								}
							} catch (e) {
								// Se il parsing fallisce, usa l'answer come fallback
								console.warn('Failed to parse answer_json:', e);
							}
						}
						
						// Se non abbiamo ancora un valore da answer_json, prova a mappare l'answer
						if (!(formField.name in savedValues) && savedAnswer.answer) {
							if (formField.type === 'checkbox') {
								savedValues[formField.name] = savedAnswer.answer === 'Sì' || savedAnswer.answer === 'true';
							} else if (['radio', 'select'].includes(formField.type || '')) {
								// Per radio/select, cerca il value corrispondente alla label salvata
								const matchingChoice = formField.choices?.find(
									(c) => c.text === savedAnswer.answer || c.value === savedAnswer.answer
								);
								if (matchingChoice) {
									savedValues[formField.name] = matchingChoice.value;
								} else if (savedAnswer.answer.startsWith('Altro:')) {
									// Se è una risposta "Altro", estrai il testo
									savedValues[formField.name] = 'other';
									const otherText = savedAnswer.answer.replace(/^Altro:\s*/, '');
									if (otherText) {
										savedValues[`${formField.name}_other`] = otherText;
									}
								} else {
									// Fallback: usa l'answer come valore
									savedValues[formField.name] = savedAnswer.answer;
								}
							} else if (formField.type === 'checkbox_group') {
								// Per checkbox_group, prova a parsare le label separate da \n
								const labels = savedAnswer.answer.split('\n');
								const values: string[] = [];
								for (const label of labels) {
									if (label.startsWith('Altro:')) {
										values.push('other');
										const otherText = label.replace(/^Altro:\s*/, '');
										if (otherText) {
											savedValues[`${formField.name}_other`] = otherText;
										}
									} else {
										const matchingChoice = formField.choices?.find((c) => c.text === label);
										if (matchingChoice) {
											values.push(matchingChoice.value);
										}
									}
								}
								if (values.length > 0) {
									savedValues[formField.name] = values;
								}
							} else {
								// Per text, textarea, etc.: usa direttamente l'answer
								savedValues[formField.name] = savedAnswer.answer;
							}
						}
					}
				}
			}
		}
	} catch (err) {
		console.warn('Failed to load saved answers:', err);
		// Ritorna oggetto vuoto se il caricamento fallisce
	}

	return savedValues;
};

// Calcola il risultato
const calculateResult = async () => {
	if (!wizard.value || !sessionSecretHash.value) return;

	isLoading.value = true;
	error.value = null;

	try {
		const response = await $fetch<{
			result: WizardResult;
			points_score: Record<number, number>;
			debug_logs?: string[];
		}>('/api/wizard/calculate-result', {
			method: 'POST',
			body: {
				session_secret_hash: sessionSecretHash.value,
				wizard_id: wizard.value.id,
			},
		});

		// Log i debug logs se presenti
		if (response.debug_logs && response.debug_logs.length > 0) {
			console.log('=== WIZARD CALCULATION DEBUG LOGS ===');
			response.debug_logs.forEach((log) => console.log(log));
			console.log('=== END DEBUG LOGS ===');
		}

		result.value = response.result;
		showContactForm.value = true;
		
		// Scrolla all'inizio della pagina quando si mostra il risultato
		scrollToTop();
	} catch (err: any) {
		error.value = err.message || 'Errore nel calcolo del risultato';
	} finally {
		isLoading.value = false;
	}
};

// Torna allo step precedente
const goToPreviousStep = async () => {
	if (currentStepIndex.value > 0 && wizard.value && sessionSecretHash.value) {
		isGoingBack.value = true; // Disabilita il watcher
		const previousIndex = currentStepIndex.value - 1;
		const previousStep = wizard.value.steps?.[previousIndex];
		
		if (!previousStep || typeof previousStep === 'string') {
			isGoingBack.value = false;
			return;
		}

		const previousStepId = previousStep.id;
		currentStepIndex.value = previousIndex;
		
		// Aspetta che initialValues si aggiorni per il nuovo step
		await nextTick();
		
		// Recupera le risposte salvate per lo step precedente
		const previousStepQuestions = (previousStep.questions || []).filter(
			(q): q is WizardStepQuestion => typeof q === 'object'
		);
		const savedValues = await loadSavedAnswersForStep(previousStepId, previousStepQuestions);

		// Combina i valori iniziali con quelli salvati
		const newInitialValues = { ...initialValues.value, ...savedValues };
		
		// Reset form con i valori combinati
		resetForm({
			values: newInitialValues,
		});
		
		// Forza l'aggiornamento dei valori per assicurarsi che siano sincronizzati
		await nextTick();
		for (const [key, val] of Object.entries(newInitialValues)) {
			setFieldValue(key, val);
		}
		
		// Aspetta un altro tick per assicurarsi che tutto sia sincronizzato
		await nextTick();
		
		// Riabilita il watcher solo dopo che tutto è stato aggiornato
		isGoingBack.value = false;
		
		// Scrolla all'inizio della pagina
		scrollToTop();
	}
};

// Salva i dati di contatto
const saveContactInfo = async (contactData: {
	full_name: string;
	email: string;
	phone?: string;
	preferred_contact_type?: 'phone' | 'email';
	privacy_consent: boolean;
	promotion_consent?: boolean;
}) => {
	if (!wizard.value || !sessionSecretHash.value) return;

	isSubmittingContact.value = true;
	error.value = null;

	try {
		await $fetch('/api/wizard/submission-contact', {
			method: 'POST',
			body: {
				session_secret_hash: sessionSecretHash.value,
				wizard_id: wizard.value.id,
				...contactData,
			},
		});

		contactSubmitted.value = true;
	} catch (err: any) {
		error.value = err.message || 'Errore nel salvataggio dei dati di contatto';
	} finally {
		isSubmittingContact.value = false;
	}
};

// Form per i dati di contatto - usa ref invece di useForm per evitare problemi con readonly
const contactFormData = ref({
	full_name: '',
	email: '',
	phone: '',
	preferred_contact_type: undefined as 'phone' | 'email' | undefined,
	privacy_consent: false,
	promotion_consent: false,
});

const contactFormErrors = ref<Record<string, string>>({});

// Validazione manuale del form di contatto
const validateContactForm = () => {
	contactFormErrors.value = {};
	let isValid = true;

	if (!contactFormData.value.full_name.trim()) {
		contactFormErrors.value.full_name = 'Nome obbligatorio';
		isValid = false;
	}

	if (!contactFormData.value.email.trim()) {
		contactFormErrors.value.email = 'Email obbligatoria';
		isValid = false;
	} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactFormData.value.email)) {
		contactFormErrors.value.email = 'Email non valida';
		isValid = false;
	}

	if (!contactFormData.value.privacy_consent) {
		contactFormErrors.value.privacy_consent = 'Consenso privacy obbligatorio';
		isValid = false;
	}

	return isValid;
};

const onSubmitContact = async () => {
	if (!validateContactForm()) {
		return;
	}

	await saveContactInfo({
		full_name: contactFormData.value.full_name,
		email: contactFormData.value.email,
		phone: contactFormData.value.phone || undefined,
		preferred_contact_type: contactFormData.value.preferred_contact_type,
		privacy_consent: contactFormData.value.privacy_consent,
		promotion_consent: contactFormData.value.promotion_consent,
	});
};

const wizardImageUrl = computed(() => {
	if (!wizard.value?.image) return null;
	const imageId = typeof wizard.value.image === 'string' 
		? wizard.value.image 
		: wizard.value.image.id;
	return getDirectusAssetURL(imageId);
});

const stepImageUrl = computed(() => {
	if (!currentStep.value?.image) return null;
	const imageId = typeof currentStep.value.image === 'string' 
		? currentStep.value.image 
		: currentStep.value.image.id;
	return getDirectusAssetURL(imageId);
});

const resultImageUrl = computed(() => {
	if (!result.value?.image) return null;
	const imageId = typeof result.value.image === 'string' 
		? result.value.image 
		: result.value.image.id;
	return getDirectusAssetURL(imageId);
});
</script>

<template>
	<section ref="wizardContainer" class="w-full h-screen fixed inset-0 overflow-y-auto">
		<div class="w-full h-full flex flex-col">
			<!-- Loading -->
			<div v-if="isLoading && !wizard" class="flex justify-center items-center h-full">
				<Loader2 class="w-8 h-8 animate-spin text-accent" />
			</div>

			<!-- Error -->
			<div v-if="error" class="p-4 m-4 text-red-500 bg-red-100 rounded-md">
				<strong>Errore:</strong> {{ error }}
			</div>

			<!-- Step iniziale -->
			<div v-if="wizard && currentStepIndex === -1" class="w-full h-full">
				<div class="flex flex-col lg:flex-row h-full">
					<!-- Colonna sinistra: contenuti -->
					<div class="flex-1 flex flex-col p-8 lg:p-16 overflow-y-auto bg-white">
						<div class="max-w-2xl mx-auto w-full flex flex-col h-full">
							<!-- Logo in alto -->
							<div class="mb-8 flex justify-center lg:justify-start">
								<NuxtLink to="/" class="flex-shrink-0 transition-opacity hover:opacity-70">
									<img :src="lightLogoUrl" alt="Logo" class="w-[160px] h-auto dark:hidden" width="200" height="133" />
									<img
										v-if="darkLogoUrl"
										:src="darkLogoUrl"
										alt="Logo (Dark Mode)"
										class="w-[160px] h-auto hidden dark:block"
										width="200"
										height="133"
									/>
								</NuxtLink>
							</div>

							<!-- Contenuto principale centrato -->
							<div class="flex-1 flex flex-col justify-center text-center lg:text-left">
								<h1 v-if="wizard.title" class="font-heading text-3xl md:text-4xl font-bold mb-4 text-black text-left" style="font-family: 'Oswald', sans-serif; letter-spacing: 0.3em;">
									{{ wizard.title }}
								</h1>

								<div v-if="wizard.description" class="prose prose-lg mx-auto lg:mx-0 mb-8 text-black" v-html="wizard.description" />

								<div class="flex justify-center lg:justify-start">
									<BaseButton
										:id="`wizard-start-${wizard.id}`"
										:label="wizard.start_button_label || 'Inizia'"
										variant="secondary"
										icon="arrow"
										icon-position="right"
										:disabled="isLoading"
										@click="startWizard"
									/>
								</div>
							</div>

							<!-- Disclaimer in fondo -->
							<div v-if="wizard.disclaimer" class="mt-auto pt-8 text-sm text-gray-600 text-center lg:text-left" v-html="wizard.disclaimer" />
						</div>
					</div>

					<!-- Colonna destra: immagine come sfondo -->
					<div 
						class="flex-1 hidden lg:block"
						:style="wizardImageUrl ? {
							backgroundImage: `url(${wizardImageUrl})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						} : {
							backgroundColor: '#f3f4f6',
						}"
					>
						<div v-if="!wizardImageUrl" class="h-full flex items-center justify-center">
							<p class="text-gray-400">Nessuna immagine</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Step del questionario -->
			<div v-if="wizard && currentStepIndex >= 0 && !result" class="w-full lg:h-full">
				<div class="flex flex-col lg:flex-row lg:h-full">
					<!-- Colonna sinistra: contenuti -->
					<div class="flex-1 flex flex-col p-8 lg:p-16 bg-white lg:overflow-hidden">
						<div class="max-w-2xl mx-auto w-full flex flex-col lg:min-h-full">
							<!-- Step content -->
							<div v-if="currentStep" class="flex flex-col lg:flex-1">
								<h2 v-if="currentStep.title" class="text-xl md:text-2xl font-light mb-6 text-black" style="font-family: 'Oswald', sans-serif; letter-spacing: 0.3em;">
									{{ currentStep.title }}
								</h2>

								<form v-if="validationSchema" @submit.prevent="onSubmitForm" class="flex flex-col lg:flex-1">
									<div class="space-y-10">
										<template v-for="field in validFields" :key="field.id">
											<BaseFormField
												:field="field"
												:model-value="values[field.name]"
											/>
											<!-- Campo "Altro" per radio/select/checkbox_group quando "other" è selezionato -->
											<div v-if="field.choices?.some((c) => c.value === 'other') && (
												(['radio', 'select'].includes(field.type || '') && values[field.name] === 'other') ||
												(field.type === 'checkbox_group' && Array.isArray(values[field.name]) && values[field.name].includes('other'))
											)" class="mt-4">
												<label :for="`${field.name}_other`" class="block text-sm font-medium mb-1 text-black">
													Specifica
												</label>
												<input
													:id="`${field.name}_other`"
													:value="values[`${field.name}_other`] || ''"
													@input="setFieldValue(`${field.name}_other`, ($event.target as HTMLInputElement).value)"
													@blur="handleInputBlur"
													type="text"
													class="w-full px-3 py-2 border border-gray-300 rounded-md text-black"
													placeholder="Inserisci il testo"
												/>
											</div>
										</template>
									</div>

									<div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
										<BaseButton
											v-if="currentStepIndex > 0"
											:id="`wizard-back-${currentStep?.id}`"
											label="Indietro"
											variant="outline"
											:custom-icon="ArrowLeft"
											icon-position="left"
											:disabled="isLoading"
											@click.prevent="goToPreviousStep"
										/>
										<div v-else />

										<BaseButton
											:id="`wizard-next-${currentStep?.id}`"
											:label="isLastStep ? 'Calcola risultato' : 'Avanti'"
											variant="secondary"
											icon="arrow"
											icon-position="right"
											:disabled="isLoading"
											type="submit"
										>
											<Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
										</BaseButton>
									</div>
								</form>
							</div>
							
							<!-- Progress bar in basso - sempre dopo i pulsanti -->
							<div class="mt-8 pt-8 pb-safe max-w-2xl mx-auto w-full">
								<div class="flex justify-between items-center mb-2">
									<span class="text-sm text-gray-600">
										Step {{ currentStepIndex + 1 }} di {{ wizard.steps?.length || 0 }}
									</span>
									<span class="text-sm text-gray-600">
										{{ Math.round(((currentStepIndex + 1) / (wizard.steps?.length || 1)) * 100) }}%
									</span>
								</div>
								<div class="w-full bg-gray-200 rounded-full h-2">
									<div
										class="bg-accent h-2 rounded-full transition-all duration-300"
										:style="{ width: `${((currentStepIndex + 1) / (wizard.steps?.length || 1)) * 100}%` }"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- Colonna destra: immagine come sfondo -->
					<div 
						class="flex-1 hidden lg:block"
						:style="stepImageUrl ? {
							backgroundImage: `url(${stepImageUrl})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						} : {
							backgroundColor: '#f3f4f6',
						}"
					>
						<div v-if="!stepImageUrl" class="h-full flex items-center justify-center">
							<p class="text-gray-400">Nessuna immagine</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Risultato -->
			<div v-if="result" class="w-full h-full">
				<div class="flex flex-col lg:flex-row h-full">
					<!-- Colonna sinistra: contenuti -->
					<div class="flex-1 flex flex-col p-8 lg:p-16 overflow-y-auto bg-white">
						<div class="max-w-2xl mx-auto w-full flex flex-col h-full">
							<!-- Logo in alto -->
							<div class="mb-8 flex justify-center lg:justify-start">
								<NuxtLink to="/" class="flex-shrink-0 transition-opacity hover:opacity-70">
									<img :src="lightLogoUrl" alt="Logo" class="w-[160px] h-auto dark:hidden" width="200" height="133" />
									<img
										v-if="darkLogoUrl"
										:src="darkLogoUrl"
										alt="Logo (Dark Mode)"
										class="w-[160px] h-auto hidden dark:block"
										width="200"
										height="133"
									/>
								</NuxtLink>
							</div>

							<!-- Contenuto principale centrato -->
							<div class="flex-1 flex flex-col justify-center text-center lg:text-left">
								<h2 v-if="result.title" class="text-2xl md:text-3xl font-bold mb-4 text-black flex items-center gap-3 justify-start">
									<span v-if="result.icon" class="material-symbols-outlined text-accent" style="font-size: 2.5rem;">
										{{ result.icon }}
									</span>
									{{ result.title }}
								</h2>

								<div v-if="result.description" class="prose prose-base mx-auto lg:mx-0 mb-4 text-black prose-p:text-black prose-headings:text-black prose-strong:text-black prose-em:text-black" v-html="result.description" />

								<!-- Form contatto -->
								<div v-if="showContactForm && !contactSubmitted" class="mt-4 w-full">
									<div class="prose prose-base mx-auto lg:mx-0 mb-4 text-black" v-html="'Prenota un appuntamento gratuito in sede o da remoto per conoscerci di persona ed elaborare un piano personalizzato sulla base delle tue esigenze.'" />
								<form @submit.prevent="onSubmitContact" class="space-y-4 text-left w-full">
									<div class="w-full">
										<label class="block text-sm font-medium mb-1 text-black">Nome *</label>
										<input
											v-model="contactFormData.full_name"
											type="text"
											@blur="handleInputBlur"
											class="w-full px-3 py-2 border rounded-md"
											:class="contactFormErrors.full_name ? 'border-red-500' : 'border-gray-300'"
										/>
										<p v-if="contactFormErrors.full_name" class="text-red-500 text-sm mt-1">
											{{ contactFormErrors.full_name }}
										</p>
									</div>

									<div class="w-full flex flex-col sm:flex-row gap-4">
										<div class="flex-1">
											<label class="block text-sm font-medium mb-1 text-black">Email *</label>
											<input
												v-model="contactFormData.email"
												type="email"
												@blur="handleInputBlur"
												class="w-full px-3 py-2 border rounded-md"
												:class="contactFormErrors.email ? 'border-red-500' : 'border-gray-300'"
											/>
											<p v-if="contactFormErrors.email" class="text-red-500 text-sm mt-1">
												{{ contactFormErrors.email }}
											</p>
										</div>

										<div class="flex-1">
											<label class="block text-sm font-medium mb-1 text-black">Telefono</label>
											<input
												v-model="contactFormData.phone"
												type="tel"
												@blur="handleInputBlur"
												class="w-full px-3 py-2 border border-gray-300 rounded-md"
											/>
										</div>
									</div>

									<div class="w-full">
										<label class="block text-sm font-medium mb-1 text-black">Verrai ricontattato da WeFocus per confermare il tuo appuntamento. Come preferisci essere ricontattato?</label>
										<RadioGroup v-model="contactFormData.preferred_contact_type" class="mt-2 flex flex-row gap-4">
											<div class="flex items-center gap-x-2">
												<RadioGroupItem id="preferred-email" value="email" />
												<Label for="preferred-email" class="text-sm text-black cursor-pointer">
													Email
												</Label>
											</div>
											<div class="flex items-center gap-x-2">
												<RadioGroupItem id="preferred-phone" value="phone" />
												<Label for="preferred-phone" class="text-sm text-black cursor-pointer">
													Telefono
												</Label>
											</div>
										</RadioGroup>
									</div>

									<div class="flex items-center gap-x-2 w-full">
										<Checkbox
											id="privacy-consent"
											v-model:checked="contactFormData.privacy_consent"
										/>
										<Label for="privacy-consent" class="text-sm text-black">
											Accetto l'informativa sulla privacy *
										</Label>
									</div>
									<p v-if="contactFormErrors.privacy_consent" class="text-red-500 text-sm">
										{{ contactFormErrors.privacy_consent }}
									</p>

									<div class="flex items-center gap-x-2 w-full">
										<Checkbox
											id="promotion-consent"
											v-model:checked="contactFormData.promotion_consent"
										/>
										<Label for="promotion-consent" class="text-sm text-black">
											Accetto di ricevere comunicazioni promozionali
										</Label>
									</div>

									<div class="flex flex-col sm:flex-row gap-4">
										<BaseButton
											:id="`wizard-contact-submit-${result?.id}`"
											:label="isSubmittingContact ? 'Invio in corso...' : 'Prenota appuntamento'"
											variant="secondary"
											icon="arrow"
											icon-position="right"
											:disabled="isSubmittingContact"
											class="flex-1"
											@click="onSubmitContact"
										>
											<Loader2 v-if="isSubmittingContact" class="w-4 h-4 animate-spin" />
										</BaseButton>
										<BaseButton
											:id="`wizard-close-${result?.id}`"
											label="Chiudi"
											variant="outline"
											:disabled="isSubmittingContact"
											className="flex-1 text-black"
											@click="navigateTo('/')"
										/>
									</div>
								</form>
								</div>

							<!-- Messaggio di conferma -->
							<div v-if="contactSubmitted" class="mt-8 flex flex-col items-center gap-4">
								<CheckCircle class="w-12 h-12 text-black" />
								<p class="text-lg text-gray-700 text-center">
									Grazie! Verrai ricontattato al più presto dal nostro staff<br />
									per confermare il tuo appuntamento.
								</p>
								<BaseButton
									:id="`wizard-close-after-submit-${result?.id}`"
									label="Chiudi"
									variant="outline"
									className="mt-4 text-black"
									@click="navigateTo('/')"
								/>
								</div>
							</div>
						</div>
					</div>

					<!-- Colonna destra: immagine come sfondo -->
					<div 
						class="flex-1 hidden lg:block"
						:style="resultImageUrl ? {
							backgroundImage: `url(${resultImageUrl})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						} : {
							backgroundColor: '#f3f4f6',
						}"
					>
						<div v-if="!resultImageUrl" class="h-full flex items-center justify-center">
							<p class="text-gray-400">Nessuna immagine</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

